
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoIcon } from 'lucide-react';

interface AddSiteFormProps {
  onAddSite: (title: string, url: string, description: string, initialCredits: number) => Promise<any>;
  userCredits: number;
}

export const AddSiteForm = ({ onAddSite, userCredits }: AddSiteFormProps) => {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Coût fixe d'un crédit par site
  const initialCredits = 1;
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title || !url) return;
    
    setIsSubmitting(true);
    
    try {
      await onAddSite(title, url, description, initialCredits);
      // Réinitialiser le formulaire
      setTitle('');
      setUrl('');
      setDescription('');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const insufficientCredits = userCredits < initialCredits;
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Ajouter un site</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="site-title">
              Titre du site
            </label>
            <Input
              id="site-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Mon blog personnel"
              required
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="site-url">
              URL du site
            </label>
            <Input
              id="site-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Ex: https://monsite.com"
              required
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="site-description">
              Description (optionnelle)
            </label>
            <Textarea
              id="site-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brève description de votre site"
              rows={3}
            />
          </div>
          
          <Alert className="bg-blue-50 text-blue-800 border-blue-200">
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>
              L'ajout d'un site coûte {initialCredits} crédit. Vous recevrez des visites tant que le site aura des crédits.
            </AlertDescription>
          </Alert>
          
          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting || insufficientCredits}
          >
            {isSubmitting ? "Ajout en cours..." : `Ajouter (Coût: ${initialCredits} crédit)`}
          </Button>
          
          {insufficientCredits && (
            <p className="text-sm text-red-500 text-center">
              Vous n'avez pas assez de crédits. Visitez d'autres sites pour en gagner.
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
